import * as currency from 'currency-formatter';
import { useRef } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { parseCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { type ColSpan, lgSpan, mdSpan, smSpan } from './form-input';

type FormMaskedInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;
  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;
  className?: string;
} & React.ComponentProps<'input'>;

export function FormCurrencyInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  sm = 12,
  md = 4,
  lg = 4,
  className,
  ...props
}: FormMaskedInputProps<T>) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const moveCursor = (el: HTMLInputElement, digitIndex: number) => {
    const formatted = el.value;

    let digitCount = 0;
    let pos = formatted.length;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        if (digitCount === digitIndex) {
          pos = i;
          break;
        }
        digitCount++;
      }
    }

    el.setSelectionRange(pos, pos + 1);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(sm && smSpan[sm], md && mdSpan[md], lg && lgSpan[lg], className)}
          aria-required
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>
            {label}
            {props?.required && <strong className="text-red-600">*</strong>}
          </FieldLabel>

          <InputGroup>
            <InputGroupInput
              {...field}
              {...props}
              ref={(el) => {
                inputRef.current = el;
                field.ref(el);
              }}
              value={
                field.value
                  ? currency.format(field.value, { code: 'BRL', symbol: '' })
                  : '0,00'
              }
              id={name}
              type="text"
              autoComplete={autoComplete}
              aria-invalid={fieldState.invalid}
              onClick={() => {
                const el = inputRef.current;
                if (!el) return;

                const pos = el.selectionStart ?? 0;
                el.setSelectionRange(pos, pos + 1);
              }}
              onKeyDown={(e) => {
                const el = inputRef.current;
                if (!el) return;

                const raw = el.value;
                const digits = raw.replace(/\D/g, '').split('');

                const start = el.selectionStart ?? 0;

                let digitIndex = raw.slice(0, start).replace(/\D/g, '').length;

                if (e.key === 'ArrowRight') {
                  e.preventDefault();

                  digitIndex++;

                  moveCursor(el, digitIndex);
                  return;
                }

                if (e.key === 'ArrowLeft') {
                  e.preventDefault();

                  digitIndex = Math.max(0, digitIndex - 1);

                  moveCursor(el, digitIndex);
                  return;
                }

                if (/^\d$/.test(e.key)) {
                  e.preventDefault();

                  digits[digitIndex] = e.key;

                  const newNumber = Number(digits.join('')) / 100;
                  field.onChange(newNumber);

                  requestAnimationFrame(() => {
                    moveCursor(el, digitIndex + 1);
                  });

                  return;
                }
              }}
              onChange={(e) => {
                const parsed = parseCurrency(e.target.value);
                field.onChange(parsed);
              }}
            />

            <InputGroupAddon
              align="inline-start"
              className="text-extrabold text-gray-300 dark:text-gray-500"
            >
              R$
            </InputGroupAddon>
          </InputGroup>

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
